import { Link, Outlet, useNavigate } from "react-router";
import { ArrowLeft, Check, ChevronRight } from "lucide-react";
import { CreateOrderProvider } from "./create-order-context";
import { useSession } from "@/services/auth";
import { useTranslation } from "react-i18next";

const STEPS = [
  { n: 1, labelKey: "details", path: "/new-shipment/details" },
  { n: 2, labelKey: "pricing", path: "/new-shipment/pricing" },
  { n: 3, labelKey: "payment", path: "/new-shipment/payment" },
  { n: 4, labelKey: "done", path: "/new-shipment/confirmation" },
] as const;

interface StepRailProps {
  active: 1 | 2 | 3 | 4;
}

/** Desktop step rail — 4 columns, full chrome. */
export function StepRail({ active }: StepRailProps) {
  const { t } = useTranslation("app");

  return (
    <div className="flex border-y border-default-200">
      {STEPS.map((s, i) => {
        const state =
          s.n < active ? "done" : s.n === active ? "current" : "todo";
        return (
          <div
            key={s.n}
            className={`flex-1 px-6 py-4 flex items-center gap-3.5 ${
              i > 0 ? "border-l border-default-200" : ""
            } ${state === "current" ? "bg-default-50" : "bg-background"}`}
          >
            <span
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${
                state === "done"
                  ? "bg-primary text-white"
                  : state === "current"
                    ? "bg-default-900 text-white"
                    : "bg-background text-default-500 ring-1 ring-default-300"
              }`}
            >
              {state === "done" ? <Check className="h-3.5 w-3.5" /> : s.n}
            </span>
            <div>
              <div className="text-tiny uppercase tracking-wider font-semibold text-default-500">
                {t("createShipment.step", { step: s.n })}
              </div>
              <div
                className={`text-sm font-semibold mt-0.5 ${
                  state === "todo" ? "text-default-500" : "text-default-900"
                }`}
              >
                {t(`createShipment.steps.${s.labelKey}`)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Mobile compact header: back arrow + 'Step X of 4' + Exit + hairline progress. */
function MobileStepHeader({ step }: { step: 1 | 2 | 3 | 4 }) {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const previousPath = step > 1 ? STEPS[step - 2].path : "/dashboard";
  const progress = (step / 4) * 100;
  return (
    <div className="px-5 pt-4 pb-3">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => void navigate(previousPath)}
          className="p-1 -ml-1 text-default-700"
          aria-label={t("createShipment.back")}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("createShipment.stepOf", { step })}
        </div>
        <Link
          to="/dashboard"
          className="text-xs text-primary hover:underline font-medium"
        >
          {t("createShipment.exit")}
        </Link>
      </div>
      <div className="h-[3px] bg-default-100 rounded-sm overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

interface CreateShellProps {
  step: 1 | 2 | 3 | 4;
  eyebrow?: string;
  title: string;
  italic: string;
  lede: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function CreateShell({
  step,
  eyebrow,
  title,
  italic,
  lede,
  children,
  footer,
}: CreateShellProps) {
  const { t } = useTranslation("app");

  return (
    <div className="-mx-4 md:-mx-8 -mt-6 md:mb-0 min-h-[calc(100vh-200px)] flex flex-col bg-default-50">
      <div className="flex-1 max-w-[1240px] w-full mx-auto md:my-6 px-0 md:px-6">
        <div className="bg-background md:rounded-2xl md:border md:border-default-200 md:shadow-sm overflow-hidden flex flex-col min-h-full">
          {/* Mobile: hairline progress + back/exit chrome */}
          <div className="md:hidden border-b border-default-200">
            <MobileStepHeader step={step} />
          </div>

          <div className="px-5 md:px-12 pt-5 md:pt-10 pb-5 md:pb-6">
            <div className="hidden md:flex items-center gap-2 text-sm text-default-500 mb-4">
              <Link
                to="/dashboard"
                className="hover:text-foreground hover:underline"
              >
                {t("createShipment.shipments")}
              </Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-default-700">
                {t("createShipment.newShipment")}
              </span>
            </div>

            <div className="hidden md:block text-tiny font-semibold uppercase tracking-wider text-default-500 mb-3.5">
              {eyebrow ?? t("createShipment.defaultEyebrow", { step })}
            </div>
            <h1 className="text-[28px] md:text-[56px] font-bold leading-[1.02] tracking-[-0.035em] md:max-w-[720px]">
              {title} <em className="text-primary">{italic}</em>
            </h1>
            <p className="mt-3 md:mt-4 max-w-[560px] text-sm md:text-[17px] leading-normal text-default-600">
              {lede}
            </p>
          </div>

          {/* Desktop step rail (mobile uses hairline progress instead) */}
          <div className="hidden md:block px-12">
            <StepRail active={step} />
          </div>

          <div className="flex-1 px-5 md:px-12 pt-5 md:pt-8 pb-8">
            {children}
          </div>

          {footer && (
            <div className="sticky bottom-0 border-t border-default-200 bg-background/95 backdrop-blur px-5 md:px-12 py-3 md:py-4 flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CreateOrderLayout() {
  const { customer, isLoggedIn } = useSession();
  return (
    <CreateOrderProvider
      customerId={isLoggedIn && customer ? customer.id : null}
    >
      <Outlet />
    </CreateOrderProvider>
  );
}
